import { Injectable } from '@angular/core'
import { ColumnCatalogEntry } from '../models/source-definition.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * Marks a discovered Aggregate Info column whose key the server's catalog did NOT list, so an
 * unmodeled key is never mistaken for a first-class column of the schema. Keys the catalog already
 * named keep the server-resolved label verbatim (no prefix).
 */
export const AGG_LABEL_PREFIX = '{ }.'

const DEFAULT_AGG_WIDTH = 160

/** Split a camelCase / PascalCase key into words. */
const splitCamelCase = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')

/**
 * Underscore-delimited keys are already word-separated; anything else is treated as camelCase.
 * Matches the prototype's labelling so a key reads the same here as it does in the design.
 */
export const humanizeAggregateKey = (key: string): string =>
  key.includes('_') ? key.replace(/_/g, ' ') : splitCamelCase(key)

/**
 * Prometheus dumps unmodeled key/value pairs into an AggregateInformation blob whose keys aren't
 * known ahead of time. We discover the union of keys across the visible rows at runtime and expose
 * each as a dynamic column, hidden by default so a wide blob doesn't flood the grid.
 *
 * Discovery order is first-seen across rows, so the Custody and Trading variants contribute their
 * own keys in a stable order rather than being sorted into an unfamiliar one.
 */
@Injectable()
export class AggregateInfoService {
  /**
   * Discover the union of AggregateInformation keys across the visible rows and expose each as a
   * dynamic column keyed by its raw `field` (col-def-factory routes it to `row.aggregateInfo[field]`
   * by its `Aggregate Info` colSource — there is no `agg:` field scheme any more). Keys the server
   * catalog already listed (`catalogFields`) keep the server label verbatim; only unlisted keys get
   * the `{ }.` label prefix. Hidden by default so a wide blob doesn't flood the grid.
   */
  discoverColumns(
    rows: WorkbenchRow[],
    catalogFields: ReadonlySet<string> = new Set<string>(),
  ): ColumnCatalogEntry[] {
    const seen = new Set<string>()
    const keys: string[] = []
    rows.forEach(row => {
      if (!row.aggregateInfo) return
      Object.keys(row.aggregateInfo).forEach(key => {
        // A key the catalog already surfaced as its own column is not re-added by discovery.
        if (!seen.has(key) && !catalogFields.has(key)) {
          seen.add(key)
          keys.push(key)
        }
      })
    })
    return keys.map(key => ({
      field: key,
      headerName: `${AGG_LABEL_PREFIX}${humanizeAggregateKey(key)}`,
      cellType: 'text',
      colSource: 'Aggregate Info',
      hide: true,
      width: DEFAULT_AGG_WIDTH,
    }))
  }
}
